var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { isPast } from "date-fns";
import { format } from "date-fns";
import { IdentifiedPatientState } from "healthex-common/consent.dtos";
import { SHLLinkStatus } from "healthex-common/shl.dtos";
import { computed, observable } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { SHLUIManager } from "../Logic/SHLUIManager";
import { PatientConsentFlow } from "./PatientConsentUIManager";
import { NamedRoutes } from "./NamedRoutes";
import { getObservableT, i18nGetDateFnsLocale } from "../Infra/i18next";
import { getTranslatedStudyText } from "./TranslationService";
import { getPatientDateString, getPatientRequestText, getPatientStatusChip } from "../patient.utils";
import { ProjectType } from "healthex-common/study.dtos";
const t = getObservableT("patients");
export class PatientStudiesListViewModel {
    constructor(container) {
        this.studyDetailsDialogOpen = false;
        this.studyDetailsStudyId = null;
        this.studyDetailsShowInterested = false;
        this.inactiveLinksExpanded = false;
        this.patientResearchDashboardUIManager = container.patientResearchDashboardUIManager;
        this.patientConsentUIManager = container.patientConsentUIManager;
        this.patientPreferencesManager = container.patientPreferencesManager;
        this.browserLanguageUIManager = container.browserLanguageUIManager;
        this.authManager = container.authManager;
        this.routingUIManager = container.routingUIManager;
        this.shlUIManager = container.shlUIManager;
    }
    get patientSHLs() {
        return this.shlUIManager.patientSHLsForSearch.value;
    }
    get activeSHLs() {
        if (this.patientSHLs === NOT_LOADED || !Array.isArray(this.patientSHLs))
            return [];
        return this.patientSHLs
            .filter((shl) => shl.status === SHLLinkStatus.ACTIVE && !isPast(new Date(shl.expiresAt)))
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    }
    get inactiveSHLs() {
        if (this.patientSHLs === NOT_LOADED || !Array.isArray(this.patientSHLs))
            return [];
        return this.patientSHLs
            .filter((shl) => shl.status !== SHLLinkStatus.ACTIVE || isPast(new Date(shl.expiresAt)))
            .sort((a, b) => {
            const getRelevantDate = (shl) => {
                if (shl.revokedAt)
                    return new Date(shl.revokedAt);
                if (shl.usedAt)
                    return new Date(shl.usedAt);
                return new Date(shl.expiresAt);
            };
            const dateA = getRelevantDate(a);
            const dateB = getRelevantDate(b);
            return dateB.getTime() - dateA.getTime();
        });
    }
    get hasActiveOrInactiveSHLs() {
        return this.activeSHLs.length > 0 || this.inactiveSHLs.length > 0;
    }
    setInactiveLinksExpanded(expanded) {
        this.inactiveLinksExpanded = expanded;
    }
    getSHLListItemInfo(shl, isActive) {
        var _a;
        const formatMMDDYYYY = (date) => format(new Date(date), "MM/dd/yyyy", { locale: i18nGetDateFnsLocale() });
        const title = ((_a = shl.label) === null || _a === void 0 ? void 0 : _a.trim()) ? shl.label : t("Created on {{date}}", { date: formatMMDDYYYY(shl.createdAt) });
        const scopes = SHLUIManager.removeImpliedScopes(shl.consentDataResourceScopes);
        const description = shl.fullRecordWithoutRestrictedData
            ? t("All health records included")
            : t("{{count}} data types included", {
                count: scopes.length,
            });
        let statusLine;
        if (isActive) {
            statusLine = shl.isOneTimeUse
                ? t("Active · One-time use")
                : t("Active · Expires {{date}}", { date: formatMMDDYYYY(shl.expiresAt) });
        }
        else {
            if (shl.status === SHLLinkStatus.USED) {
                statusLine = !shl.usedAt ? t("Used on unknown date") : t("Used on {{date}}", { date: formatMMDDYYYY(shl.usedAt) });
            }
            else if (shl.status === SHLLinkStatus.REVOKED) {
                statusLine = t("Deactivated by you");
            }
            else {
                statusLine = t("Expired on {{date}}", { date: formatMMDDYYYY(shl.expiresAt) });
            }
        }
        return { title, description, statusLine };
    }
    goToSHLLink(shlId) {
        this.routingUIManager.navigateTo(NamedRoutes.PATIENT_SMART_LINK_DETAILS, { shlId });
    }
    goToCreateShareLink() {
        this.routingUIManager.navigateTo(NamedRoutes.PATIENT_CREATE_SMART_LINK);
    }
    get isOptedOut() {
        var _a, _b;
        if (this.patientPreferencesManager.preferences.value === NOT_LOADED)
            return NOT_LOADED;
        return (_b = (_a = this.patientPreferencesManager.preferences.value) === null || _a === void 0 ? void 0 : _a.optedOut) !== null && _b !== void 0 ? _b : false;
    }
    get noConsentsAvailable() {
        return this.patientResearchDashboardUIManager.noConsentsAvailable;
    }
    get zeroState() {
        if (this.isOptedOut === NOT_LOADED)
            return null;
        if (this.isOptedOut) {
            return {
                title: t("You're not receiving requests right now"),
                text: t("Your health records can help {{ organizationName }} deliver data-driven, proactive care that supports your health journey\n\nYou’ve opted out of HealthEx, but you can update this anytime in your settings.", { organizationName: this.authManager.orgName }),
                buttonText: t("Join HealthEx again"),
                buttonOnClick: () => this.optInAgain(),
            };
        }
        return null;
    }
    get studies() {
        return this.patientResearchDashboardUIManager.sortedStudies;
    }
    showStudyDetailsDialog() {
        this.studyDetailsDialogOpen = true;
    }
    hideStudyDetailsDialog() {
        this.studyDetailsDialogOpen = false;
        this.studyDetailsStudyId = null;
        this.studyDetailsShowInterested = false;
    }
    async optInAgain() {
        await this.patientPreferencesManager.updatePatientPreferences({ optedOut: false });
    }
    getStudyPatientFriendlyTitle(study) {
        const translatedTitle = getTranslatedStudyText(study, "patientFriendlyTitle", this.browserLanguageUIManager.currentLanguage);
        return translatedTitle || study.patientFriendlyTitle || study.title || "";
    }
    getListItemInfo(study, consent) {
        var _a, _b;
        const actionNeeded = !!consent.pending;
        const agreedUpdateRecordsNeeded = !!consent.updateRecordsNeeded;
        const highlightActionNeeded = actionNeeded || agreedUpdateRecordsNeeded;
        const isInterested = consent.state === IdentifiedPatientState.ENROLLING_INTERESTED || consent.state === IdentifiedPatientState.ENROLLING_CONTACTED;
        const orgName = study.projectType === ProjectType.AI_DATA_USE
            ? (_a = study.aiToolName) !== null && _a !== void 0 ? _a : study.addedByOrganization.name
            : study.addedByOrganization.name;
        const orgLogo = (_b = study.organizationIconUrl) !== null && _b !== void 0 ? _b : null;
        return {
            chip: getPatientStatusChip(consent),
            title: this.getStudyPatientFriendlyTitle(study),
            requestText: getPatientRequestText(study.projectType),
            date: getPatientDateString(consent.timestamp),
            showActionNeededBadge: highlightActionNeeded,
            useBoldText: highlightActionNeeded,
            orgName,
            orgLogo,
            cta: actionNeeded ? () => this.startConsentFlow(study.id) : () => this.openPatientStudyDetailsPage(study.id, isInterested),
        };
    }
    startConsentFlow(studyId) {
        this.patientConsentUIManager.startConsentFlow(studyId, PatientConsentFlow.ENROLLMENT, null, null);
    }
    openPatientStudyDetailsPage(studyId, isInterested) {
        this.studyDetailsStudyId = studyId;
        this.studyDetailsShowInterested = isInterested;
        this.showStudyDetailsDialog();
    }
}
__decorate([
    observable
], PatientStudiesListViewModel.prototype, "studyDetailsDialogOpen", void 0);
__decorate([
    observable
], PatientStudiesListViewModel.prototype, "studyDetailsStudyId", void 0);
__decorate([
    observable
], PatientStudiesListViewModel.prototype, "studyDetailsShowInterested", void 0);
__decorate([
    observable
], PatientStudiesListViewModel.prototype, "inactiveLinksExpanded", void 0);
__decorate([
    computed
], PatientStudiesListViewModel.prototype, "patientSHLs", null);
__decorate([
    computed
], PatientStudiesListViewModel.prototype, "activeSHLs", null);
__decorate([
    computed
], PatientStudiesListViewModel.prototype, "inactiveSHLs", null);
__decorate([
    computed
], PatientStudiesListViewModel.prototype, "hasActiveOrInactiveSHLs", null);
__decorate([
    computed
], PatientStudiesListViewModel.prototype, "isOptedOut", null);
__decorate([
    computed
], PatientStudiesListViewModel.prototype, "noConsentsAvailable", null);
__decorate([
    computed
], PatientStudiesListViewModel.prototype, "zeroState", null);
__decorate([
    computed
], PatientStudiesListViewModel.prototype, "studies", null);
